import React, { useEffect, useState } from "react";
import { Users, Plus, Trash2, Save, Shield, Building2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";

export default function Usuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", password: "", nombre: "", role: "empresa", concesiones: [] });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, l] = await Promise.all([api.get("/users"), api.get("/lotes")]);
      setUsers(u.data);
      setLotes((l.data || []).map((it) => it.lote).sort());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (me?.role !== "admin") {
    return (
      <div className="crtm-card p-8 text-center" data-testid="usuarios-no-admin">
        <Shield size={40} className="mx-auto text-gray-400 mb-3" />
        <h3 className="font-heading font-semibold text-lg">Acceso restringido</h3>
        <p className="text-sm text-gray-500 mt-1">Solo el administrador puede gestionar usuarios.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/users", form);
      setForm({ username: "", password: "", nombre: "", role: "empresa", concesiones: [] });
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al crear");
    }
  };

  const saveEdit = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, {
        nombre: u.nombre,
        concesiones: u.concesiones,
        password: u._newPassword || null,
      });
      setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar usuario "${u.username}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const toggleConc = (lote) => {
    const has = form.concesiones.includes(lote);
    setForm({ ...form, concesiones: has ? form.concesiones.filter((c) => c !== lote) : [...form.concesiones, lote] });
  };

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Administración</div>
          <h1 className="section-title mt-1">Usuarios y empresas</h1>
          <p className="section-sub">Crea empresas y asigna las concesiones a las que tendrán acceso.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary" data-testid="btn-new-user">
          <Plus size={14} /> {showForm ? "Cancelar" : "Nueva empresa"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="crtm-card p-5 space-y-3" data-testid="user-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="kpi-label">Username</label>
              <input required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm" data-testid="form-username" />
            </div>
            <div>
              <label className="kpi-label">Nombre / Empresa</label>
              <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm" data-testid="form-nombre" />
            </div>
            <div>
              <label className="kpi-label">Contraseña</label>
              <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm" data-testid="form-password" />
            </div>
            <div>
              <label className="kpi-label">Rol</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white" data-testid="form-role">
                <option value="empresa">empresa</option>
                <option value="admin">admin</option>
              </select>
            </div>
          </div>
          {form.role === "empresa" && (
            <div>
              <label className="kpi-label">Concesiones asignadas</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {lotes.map((l) => {
                  const active = form.concesiones.includes(l);
                  return (
                    <button type="button" key={l} onClick={() => toggleConc(l)}
                      data-testid={`form-conc-${l}`}
                      className={"px-3 py-1.5 rounded-md text-xs font-semibold border " +
                        (active ? "bg-[#14532D] text-white border-[#14532D]" : "bg-white text-gray-700 border-gray-200")}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-900 rounded p-2 text-xs">{error}</div>}
          <button type="submit" className="btn-primary" data-testid="btn-save-user">
            <Save size={14} /> Crear usuario
          </button>
        </form>
      )}

      <div className="crtm-card overflow-hidden">
        {loading && <div className="p-6 text-sm text-gray-500">Cargando…</div>}
        {!loading && (
          <table className="crtm-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Concesiones</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isEdit = editing?.id === u.id;
                const cur = isEdit ? editing : u;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.username}`}>
                    <td className="font-semibold font-mono">{u.username}</td>
                    <td>
                      {isEdit ? (
                        <input value={cur.nombre || ""}
                          onChange={(e) => setEditing({ ...cur, nombre: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-200 rounded text-xs" />
                      ) : (u.nombre || "—")}
                    </td>
                    <td>
                      <span className={"pill " + (u.role === "admin" ? "rev" : "neutral")}>
                        {u.role === "admin" ? (<><Shield size={11} className="mr-1" /> admin</>) : (<><Building2 size={11} className="mr-1" /> empresa</>)}
                      </span>
                    </td>
                    <td>
                      {u.role === "admin" ? (
                        <span className="text-xs text-gray-400 italic">Todas</span>
                      ) : isEdit ? (
                        <div className="flex flex-wrap gap-1">
                          {lotes.map((l) => {
                            const active = (cur.concesiones || []).includes(l);
                            return (
                              <button key={l} onClick={() => {
                                const nc = active ? cur.concesiones.filter((c) => c !== l) : [...(cur.concesiones || []), l];
                                setEditing({ ...cur, concesiones: nc });
                              }} className={"px-2 py-0.5 rounded text-[10px] font-semibold border " +
                                (active ? "bg-[#14532D] text-white border-[#14532D]" : "bg-white text-gray-600 border-gray-200")}>
                                {l}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(u.concesiones || []).map((c) => (
                            <span key={c} className="pill neutral" style={{ fontSize: "10px" }}>{c}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {isEdit ? (
                        <div className="flex gap-2">
                          <button onClick={() => saveEdit(editing)} className="text-green-700 hover:text-green-900 text-xs font-semibold">
                            Guardar
                          </button>
                          <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-gray-900 text-xs">
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <button onClick={() => setEditing({ ...u })} className="text-blue-700 hover:text-blue-900 text-xs font-semibold" data-testid={`btn-edit-${u.username}`}>
                            Editar
                          </button>
                          {u.id !== me.id && (
                            <button onClick={() => remove(u)} className="text-red-700 hover:text-red-900" data-testid={`btn-del-${u.username}`}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
